import { endOfMonth, format, startOfMonth, subMonths } from "date-fns";

import { IMessage } from "@/interfaces/message.interface";
import { DateRange } from "@/types/date.types";

// Ordena mensagens por data (cópia, sem mutar) com desempate por id e sem duplicar o mesmo id
export const uniqueMessagesById = (messages: IMessage[]): IMessage[] => {
  const seen = new Set<number | string>();
  const unique: IMessage[] = [];

  for (const message of messages) {
    if (message?.id == null) {
      unique.push(message);
      continue;
    }
    if (seen.has(message.id)) continue;
    seen.add(message.id);
    unique.push(message);
  }

  return unique;
};

export const sortMessagesByDate = (messages: IMessage[]): IMessage[] => {
  return uniqueMessagesById(messages).sort((a, b) => {
    const diff = new Date(a.rowDate).getTime() - new Date(b.rowDate).getTime();
    if (diff !== 0) return diff;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
};


export const formatDate = (date: Date): string => {
  return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;
};

export const formatMessageTime = (rowDate: Date): string => {
  const date = new Date(rowDate);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strTime = hours + ":" + (minutes < 10 ? "0" : "") + minutes + " " + ampm;
  return strTime;
};

export const formatDateToDDMMYY = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth()
  const year = date.getFullYear().toString().substr(-2);
  return `${day}/${month}/${year}`;
};

export const convertIsoToBrazilianDateTime = (dateString: string, locale: string = "pt-BR") => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };

  const time = date.toLocaleTimeString(locale, timeOptions);

  return `${day}/${month}/${year} ${time}`;
};

export const getISOCurrentDateTime = () => new Date().toISOString();

export const getDateRange = (option: DateRange) => {
  const today = new Date();

  let startDate: Date;
  let endDate: Date;

  switch (option) {
    case DateRange.TODAY:
      startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      break;
    case DateRange.CURRENT_MONTH:
      startDate = startOfMonth(today);
      endDate = endOfMonth(today);
      break;
    case DateRange.CURRENT_YEAR:
      startDate = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
      endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
      break;
    case DateRange.YEAR:
      endDate = today;
      startDate = subMonths(today, 12);
      startDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1, 0, 0, 0);
      break;
    default:
      throw new Error("Invalid date range option");
  }

  return {
    startDate: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
    endDate: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
  };
};

export const formatDateToDDMMYYHH = (date: string) =>
  new Date(date).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Formato DD/MM/AAAA HH:MM:SS conforme dicionário / spec de integrações. */
export const formatDateTimeToDDMMYYYYHHMMSS = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

/** Formato exibido nos filtros de data/hora (DateTimePicker). */
export const DATETIME_FILTER_FORMAT = "DD/MM/YYYY HH:mm:ss";
